import type { ApiClient } from '@sentinel/api-client';
import { SKIP_FORBIDDEN_HEADER, SKIP_UNAUTH_HEADER } from '@sentinel/api-client';
import { UnauthenticatedError } from '../errors';

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * Discriminated result of a `/auth/login` call.
 *
 * The backend can respond with three different success-ish shapes:
 *  - 200 with tokens (when the SPA explicitly accepts JSON; otherwise tokens
 *    are set as HttpOnly cookies and the body just confirms success).
 *  - 200 with `mfa_required: true` and an `mfa_token`.
 *  - 403 with `error: "mfa_enrollment_required"` and a `setup_token`.
 *
 * Returning a discriminated union (instead of throwing a payload-carrying
 * error for one of those paths) lets callers exhaustively branch on `kind`
 * without dead branches.
 */
export type LoginResult =
  | { kind: 'tokens'; access_token: string; refresh_token: string; family_id?: string }
  | { kind: 'mfa_required'; mfa_token: string; methods: string[] }
  | {
      kind: 'mfa_enrollment_required';
      setup_token: string;
      methods: string[];
      deadline: string;
    };

export async function login(client: ApiClient, input: LoginInput): Promise<LoginResult> {
  // Send `Accept: */*` so the backend takes the cookie-setting branch (it
  // checks whether `Accept` contains `application/json`). The SPA relies on
  // server-set session cookies, not on parsing the JSON token body.
  //
  // Skip the global forbidden handler: login answers 403 for an IP-policy
  // block, a risk block, and "this account must sign in through its identity
  // provider". The login screen already names each of those.
  const { data, error, response } = await client.POST('/auth/login' as any, {
    body: input,
    headers: {
      Accept: '*/*',
      [SKIP_UNAUTH_HEADER]: '1',
      [SKIP_FORBIDDEN_HEADER]: '1',
    },
  } as any);

  // 403 with `mfa_enrollment_required` is reported as `error` by openapi-fetch.
  if (error) {
    const body = error as Record<string, unknown> | null;
    if (body && body.error === 'mfa_enrollment_required' && typeof body.setup_token === 'string') {
      return {
        kind: 'mfa_enrollment_required',
        setup_token: body.setup_token,
        methods: Array.isArray(body.methods) ? (body.methods as string[]) : [],
        deadline: typeof body.deadline === 'string' ? body.deadline : '',
      };
    }
    // openapi-fetch surfaces non-2xx as `error` (the parsed body) without a
    // `status` field. Callers (e.g. routes/login.index.tsx) branch on
    // `instanceof UnauthenticatedError` / `e.status === 401` to decide between
    // i18n keys; preserve that contract by wrapping a 401 here.
    if (response?.status === 401) {
      throw new UnauthenticatedError(error);
    }
    throw error;
  }

  const body = (data as Record<string, unknown> | null) ?? null;

  if (response?.status === 200 && body?.mfa_required === true && typeof body.mfa_token === 'string') {
    return {
      kind: 'mfa_required',
      mfa_token: body.mfa_token,
      methods: Array.isArray(body.methods) ? (body.methods as string[]) : [],
    };
  }

  // Default path: tokens (may be empty strings when the server set cookies
  // instead of returning a JSON token body — callers that care should look at
  // session state, not these fields).
  const tokens: LoginResult = {
    kind: 'tokens',
    access_token: typeof body?.access_token === 'string' ? body.access_token : '',
    refresh_token: typeof body?.refresh_token === 'string' ? body.refresh_token : '',
  };
  if (typeof body?.family_id === 'string') tokens.family_id = body.family_id;
  return tokens;
}
